import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { FiClock, FiSave } from "react-icons/fi";
import {
  useGetBreakSettings,
  useUpdateBreakSettings,
} from "../../services/configurationService";

const BreakSettings = () => {
  const toast = useToast();
  const [breakHours, setBreakHours] = useState("");
  const [errors, setErrors] = useState({});

  const {
    breakSettingsData,
    breakSettingsIsLoading,
    refetchBreakSettings,
  } = useGetBreakSettings({ enabled: true });

  const { updateBreakSettingsIsLoading, updateBreakSettingsPayload } =
    useUpdateBreakSettings((res) => {
      refetchBreakSettings();
      toast({
        title: "Success",
        description: "Break settings updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    });

  // Initialize form when data is loaded
  useEffect(() => {
    if (breakSettingsData?.allowed_break_hours !== undefined) {
      setBreakHours(breakSettingsData.allowed_break_hours.toString());
    } else if (!breakSettingsIsLoading) {
      // Default to 1.0 if no settings exist
      setBreakHours("1.0");
    }
  }, [breakSettingsData, breakSettingsIsLoading]);

  const validateForm = () => {
    const newErrors = {};
    const hours = parseFloat(breakHours);

    if (!breakHours || breakHours.trim() === "") {
      newErrors.breakHours = "Break hours is required";
    } else if (isNaN(hours) || hours < 0) {
      newErrors.breakHours = "Break hours must be a positive number";
    } else if (hours > 24) {
      newErrors.breakHours = "Break hours cannot exceed 24 hours";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    updateBreakSettingsPayload({
      allowed_break_hours: parseFloat(breakHours),
    });
  };

  if (breakSettingsIsLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">
          Loading break settings...
        </Text>
      </Box>
    );
  }

  return (
    <Card>
      <CardBody p={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <HStack spacing={3}>
            <Box
              p={2}
              borderRadius="8px"
              bg="blue.50"
              color="blue.600"
            >
              <FiClock size={20} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="600" color="gray.900">
                Company/Branch Break Settings
              </Text>
              <Text fontSize="sm" color="gray.600">
                Set the default allowed break hours for all employees
              </Text>
            </VStack>
          </HStack>

          {/* Info Alert */}
          <Alert status="info" borderRadius="8px">
            <AlertIcon />
            <Box>
              <Text fontSize="sm" fontWeight="500">
                How Break Hours Work
              </Text>
              <Text fontSize="xs" mt={1}>
                Employees can take breaks up to this limit without deduction. 
                Any excess break time will be deducted from their work hours. 
                Individual employees can have custom break hours set as overrides.
              </Text>
            </Box>
          </Alert>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.breakHours}>
                <FormLabel fontWeight="500" color="gray.700">
                  Allowed Break Hours (Default)
                </FormLabel>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={breakHours}
                  onChange={(e) => {
                    setBreakHours(e.target.value);
                    if (errors.breakHours) {
                      setErrors({ ...errors, breakHours: "" });
                    }
                  }}
                  placeholder="1.0"
                  size="lg"
                />
                <FormErrorMessage>{errors.breakHours}</FormErrorMessage>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Enter the number of hours (e.g., 1.0, 1.5, 2.0). 
                  Supports half-hour increments (0.5).
                </Text>
              </FormControl>

              <HStack spacing={3} justify="flex-end">
                <Button
                  type="submit"
                  colorScheme="blue"
                  leftIcon={<FiSave />}
                  isLoading={updateBreakSettingsIsLoading}
                  loadingText="Saving..."
                  size="md"
                >
                  Save Settings
                </Button>
              </HStack>
            </VStack>
          </form>

          {/* Current Setting Display */}
          {breakSettingsData && (
            <Box
              p={4}
              bg="gray.50"
              borderRadius="8px"
              border="1px solid"
              borderColor="gray.200"
            >
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Current Default:{" "}
                <Text as="span" color="blue.600" fontWeight="600">
                  {breakSettingsData.allowed_break_hours} hours
                </Text>
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Last updated:{" "}
                {breakSettingsData.updated_at
                  ? new Date(breakSettingsData.updated_at).toLocaleDateString()
                  : "N/A"}
              </Text>
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default BreakSettings;

